import { useState, useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  RefreshCw,
  Gauge,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Cpu,
  Database,
  Server,
} from 'lucide-react';
import { Badge } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ouropix/ui';
import { toast } from 'sonner';
import { SystemHealthGrid } from '@/components/admin-ops/SystemHealthGrid';
import { CircuitBreakerGrid } from '@/components/admin-ops/CircuitBreakerGrid';
import { DatabaseHealthTab } from '@/components/admin-ops/DatabaseHealthTab';
import { EngineStatusCards } from '@/components/admin-ops/EngineStatusCards';
import { useEnginesDiagnostic } from '@/hooks/useEnginesDiagnostic';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_admin/saude-sistema')({
  component: SaudeSistemaPage,
});

interface HealthData {
  status: 'healthy' | 'degraded' | 'critical';
  timestamp?: string;
}

function getStatusConfig(status?: string) {
  switch (status) {
    case 'healthy':
      return {
        icon: CheckCircle2,
        color: 'text-chart-2',
        bgColor: 'bg-chart-2/10',
        borderColor: 'border-chart-2/30',
        label: 'Saudavel',
      };
    case 'degraded':
      return {
        icon: AlertTriangle,
        color: 'text-chart-4',
        bgColor: 'bg-chart-4/10',
        borderColor: 'border-chart-4/30',
        label: 'Degradado',
      };
    case 'critical':
      return {
        icon: XCircle,
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        borderColor: 'border-destructive/30',
        label: 'Critico',
      };
    default:
      return {
        icon: Activity,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
        borderColor: 'border-muted',
        label: 'Carregando...',
      };
  }
}

function SaudeSistemaPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const lastNotifiedStatus = useRef<string | null>(null);

  const { data, isLoading, refetch, isFetching, isError } = useQuery<HealthData>({
    queryKey: ['system-health'],
    queryFn: () => api.get('/health').then((r) => r.data),
    refetchInterval: 30_000,
  });

  const { data: enginesData, refetch: refetchEngines, isFetching: isFetchingEngines } =
    useEnginesDiagnostic(60_000);

  const engineStatus = enginesData?.status;

  // Alert on status change
  useEffect(() => {
    if (!data?.status) return;
    if (data.status === lastNotifiedStatus.current) return;
    lastNotifiedStatus.current = data.status;

    if (data.status === 'critical') {
      toast.error('Sistema em Estado Crítico', {
        description: 'A carga do sistema está muito alta. Verifique os circuit breakers.',
      });
    } else if (data.status === 'degraded') {
      toast.warning('Sistema Degradado', {
        description: 'A carga do sistema está elevada. Monitore de perto.',
      });
    }
  }, [data?.status]);

  const combinedStatus = (() => {
    const hs = data?.status;
    if (hs === 'critical' || engineStatus === 'critical') return 'critical';
    if (hs === 'degraded' || engineStatus === 'degraded') return 'degraded';
    if (hs === 'healthy' && engineStatus === 'healthy') return 'healthy';
    return hs;
  })();

  const statusConfig = getStatusConfig(combinedStatus);
  const StatusIcon = statusConfig.icon;
  const isAnyLoading = isLoading || isFetching;

  const lastUpdated = data?.timestamp ? new Date(data.timestamp) : null;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div
        className={cn(
          'flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-lg border',
          statusConfig.bgColor,
          statusConfig.borderColor,
        )}
      >
        <div className="flex items-center gap-3">
          <StatusIcon className={cn('h-8 w-8', statusConfig.color)} />
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Saúde do Sistema
              <Badge className={cn(statusConfig.bgColor, statusConfig.color, 'border', statusConfig.borderColor)}>
                {statusConfig.label}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              {lastUpdated
                ? `Última atualização: ${lastUpdated.toLocaleTimeString('pt-BR')}`
                : 'Aguardando dados...'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetch();
            refetchEngines();
          }}
          disabled={isAnyLoading || isFetchingEngines}
          className="gap-2"
        >
          <RefreshCw className={cn('h-4 w-4', (isAnyLoading || isFetchingEngines) && 'animate-spin')} />
          Atualizar
        </Button>
      </div>

      {/* Error State */}
      {isError && (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
          <p className="font-medium">Erro ao carregar métricas do sistema</p>
          <p className="text-sm">Verifique se o gateway está ativo e o endpoint /health responde.</p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="overview" className="gap-2">
            <Gauge className="h-4 w-4" />
            Visao Geral
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-2">
            <Server className="h-4 w-4" />
            Servicos
          </TabsTrigger>
          <TabsTrigger value="engines" className="gap-2">
            <Cpu className="h-4 w-4" />
            Motores
            {engineStatus && engineStatus !== 'healthy' && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">!</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="database" className="gap-2">
            <Database className="h-4 w-4" />
            Banco de Dados
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <SystemHealthGrid />
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <CircuitBreakerGrid />
        </TabsContent>

        {/* Engines Tab */}
        <TabsContent value="engines" className="space-y-6">
          <EngineStatusCards />
        </TabsContent>

        {/* Database Tab */}
        <TabsContent value="database" className="space-y-6">
          <DatabaseHealthTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
